import { Component, DestroyRef, DOCUMENT, inject, Renderer2, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { hlmDialogOverlayClass } from '@spartan-ng/helm/dialog';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { hlm } from '@spartan-ng/helm/utils';

/**
 * Backdrop behind the open menu. It reuses the dialog overlay look and sits just below the CDK
 * overlay container (z-index 1000), so the whole page blurs while the menu stays sharp.
 */
const BACKDROP_CLASS = hlm(hlmDialogOverlayClass, 'fixed inset-0 z-[999]');

/**
 * "Add new part" in the app header (Figma node 83:3007). It asks how the part should be added
 * before opening the matching page, which the bottom sheet behind the old FAB used to do.
 */
@Component({
  selector: 'app-add-part-menu',
  templateUrl: './add-part-menu.html',
  imports: [RouterLink, TranslatePipe, HlmButtonImports, HlmDropdownMenuImports],
})
export class AddPartMenu {
  private readonly document = inject(DOCUMENT);
  private readonly renderer = inject(Renderer2);
  private backdrop: HTMLElement | null = null;

  /**
   * While the menu is open the button is lifted above the backdrop, so it stays sharp next to its
   * menu and still reads as the control that opened it.
   */
  protected readonly menuOpen = signal(false);

  constructor() {
    inject(DestroyRef).onDestroy(() => this.hideBackdrop());
  }

  /**
   * Blurs the rest of the page while the menu is open. The backdrop lives on <body>, outside the
   * header, so no ancestor of the menu button clips or contains it. A click on it reaches the
   * document, where the menu closes as on any outside click, and it never reaches the page below.
   */
  protected showBackdrop(): void {
    if (this.backdrop !== null) {
      return;
    }

    const backdrop: HTMLElement = this.renderer.createElement('div');
    this.renderer.setAttribute(backdrop, 'class', BACKDROP_CLASS);
    this.renderer.setAttribute(backdrop, 'data-open', '');
    this.renderer.setAttribute(backdrop, 'aria-hidden', 'true');
    this.renderer.setAttribute(backdrop, 'data-cy', 'add-part-backdrop');
    this.renderer.appendChild(this.document.body, backdrop);
    this.backdrop = backdrop;
    this.menuOpen.set(true);
  }

  protected hideBackdrop(): void {
    this.menuOpen.set(false);
    if (this.backdrop === null) {
      return;
    }

    this.renderer.removeChild(this.document.body, this.backdrop);
    this.backdrop = null;
  }
}
